import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { message } from 'antd';
import { useSelected } from '../../../../../providers/selectedContext';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import { calculateSample, updateSample } from '../../../../../api/data';
import {
  CalculateSamplePayload,
  RegressionIndexes,
  UpdateSamplePayload
} from '../../../../../@types/samples/types';

import { 
  extractCurves, RawDataPoint, RegressionResult, ChartConfig,
  generateRegressionData, calculateCoreyRegression, getChartOptions
} from '../../logic';

export const useSgnKrgRegression = () => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { selectedInfo, setSelectedInfo } = useSelected();
  const sample = selectedInfo?.sample;

  const [rawDataTable, setRawDataTable] = useState<RawDataPoint[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  
  const [sorg, setSorg] = useState<number>(0);
  
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const lastSampleIdRef = useRef<string | undefined>(undefined);
  const resetPendingRef = useRef<boolean>(false);

  useEffect(() => {
    if (sample?.k_rel_values?.gas_oil?.true_sor_g != null) {
      setSorg(sample.k_rel_values.gas_oil.true_sor_g);
    } else {
      setSorg(0);
    }
  }, [sample]);

  useEffect(() => {
    setError(null);
    if (!sample) return;

    try {
      const { swArr, kArr, swi } = extractCurves(sample, 'gas_oil_krg');

      if (swi == null || sorg == null) {
        setRawDataTable([]);
        return;
      }

      const swi_fixed = sample.input_sample_values.swi || 0;
      
      const data = generateRegressionData(swArr, kArr, swi, sorg, 'increasing', swi_fixed
        
      );
      setRawDataTable(data);

    } catch (e: any) {
      setError(e.message);
      setRawDataTable([]);
    }
  }, [sample, sorg]);

  useEffect(() => {
    if (!sample || rawDataTable.length === 0) return;

    const currentDataKeys = new Set(rawDataTable.map(d => d.key));
    const isNewSample = lastSampleIdRef.current !== sample.uid;
    lastSampleIdRef.current = sample.uid;

    setSelectedRowKeys((prevSelected) => {
      let candidates: any[] = [];
      const savedIndices = sample.regression_indexes?.sgn_krg_selected_indexes || [];

      if (isNewSample) {
        candidates = savedIndices;
        if (candidates.length === 0) return rawDataTable.map(d => d.key);
      } else {
        candidates = prevSelected.length > 0 ? prevSelected : savedIndices;
      }

      const validKeys = candidates.filter((k: any) => currentDataKeys.has(Number(k)));
      return validKeys.length > 0 ? validKeys : rawDataTable.map(d => d.key);
    });
  }, [sample, rawDataTable]);

  const handleSelectedRowKeysChange = useCallback((keys: React.Key[]) => {
    resetPendingRef.current = false;
    setSelectedRowKeys(keys);
  }, []);

  const handleReset = useCallback(() => {
    const baselineSorg = sample?.k_rel_values?.gas_oil?.true_sor_g ?? 0;
    setSorg(baselineSorg);
    setSelectedRowKeys(rawDataTable.map(point => point.key));
    resetPendingRef.current = true;
  }, [sample, rawDataTable]);

  const regressionResult = useMemo<RegressionResult | null>(() => {
    if (rawDataTable.length < 2 || selectedRowKeys.length < 2) return null;
    try {
      const indices = selectedRowKeys.map(key => Number(key));
      return calculateCoreyRegression(rawDataTable, indices);
    } catch (e) { return null; }
  }, [rawDataTable, selectedRowKeys]);

  const handleSave = useCallback(async () => {
    if (!sample || !regressionResult) return;
    setLoading(true);
    setError(null);

    try {
      const indices = selectedRowKeys.map(key => Number(key));
      const existingIndexes = sample.regression_indexes || {};
      
      const indexesToSave: RegressionIndexes = {
        ...existingIndexes,
        sgn_krg_selected_indexes: indices,
      };

      const payload: UpdateSamplePayload = {
        sample_uid: sample.uid,
        regression_indexes: resetPendingRef.current
          ? {
              ...existingIndexes,
              sgn_krg_selected_indexes: [],
              refined_no_g_selected_indexes: [],
              refined_ng_selected_indexes: [],
            }
          : indexesToSave,
        true_krg: resetPendingRef.current ? null : regressionResult.a_max_calc
      };

      const updatedSample = await updateSample(payload);
      const refreshPayload: CalculateSamplePayload = {
        sample_uid: updatedSample.uid,
        strategies_keys: ['gas-oil'],
        input_data_map: {
          'gas-oil': { type: 'GAS_OIL' }
        }
      };
      const recalculatedSample = await calculateSample(refreshPayload);
      resetPendingRef.current = false;
      setSelectedInfo({ sample: recalculatedSample });
      message.success('Gas-Oil Regression saved!');
    } catch (e: any) {
      setError(e.message || 'Failed to save.');
      message.warning(e.message);
    } finally {
      setLoading(false);
    }
  }, [sample, selectedRowKeys, regressionResult, setSelectedInfo]);

  const chartOptions = useMemo(() => {
    const chartConfig: ChartConfig = {
        title: 'Sgn vs Krg Regression',
        xAxisLabel: 'Sgn (Normalized)',
        yAxisLabel: 'Krg (Calculated)',
        tooltipLabelX: 'Sgn',
        tooltipLabelY: 'Krg',
        seriesNameRaw: 'All Lab Points (Normalized)',
        seriesNameSelected: 'Points in Regression',
        seriesNameFit: 'Krg Calc (Fit Line)',
        seriesColor: colors.gasOil,
        fitLineColor: 'orange',
        xAxisReversed: false
    };

    return getChartOptions({
        regressionResult,
        rawDataTable,
        selectedRowKeys,
        config: chartConfig
    });
  }, [regressionResult, rawDataTable, selectedRowKeys, colors]);

  return {
    sample,
    sorg,
    loading,
    error,
    rawDataTable,
    selectedRowKeys,
    setSelectedRowKeys: handleSelectedRowKeysChange,
    regressionResult,
    chartOptions,
    handleReset,
    handleSave,
    colors,
    isDay
  };
};
